import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';

const SuccessStoriesPage = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [statsRef, statsInView] = useInView({ triggerOnce: true, threshold: 0.3 });

  const featuredStory = {
    name: 'Chioma Eze',
    role: 'E-commerce Business Owner',
    service: 'E-commerce Management',
    image: 'https://public.readdy.ai/ai/img_res/1a1a6c5499b73a66583a471f8234fb45.jpg',
    quote: 'Working with Titilayo completely transformed my online store. Her strategic e-commerce guidance and hands-on support helped me triple my revenue in just 6 months. She didn\'t just give advice – she walked me through every step, from optimizing my product listings to implementing effective marketing strategies.',
    results: [
      { metric: '300%', label: 'Revenue Growth' },
      { metric: '6', label: 'Months' },
      { metric: '5★', label: 'Rating' },
    ],
  };

  const testimonials = [
    {
      name: 'Adebayo Johnson',
      role: 'Tech Startup Founder',
      service: 'Business Consulting',
      image: 'https://readdy.ai/api/search-image?query=professional%20African%20male%20entrepreneur%20in%20business%20attire%20working%20confidently%20at%20desk%20with%20laptop%20in%20modern%20startup%20office%2C%20satisfied%20expression%20showing%20business%20success%2C%20charts%20visible%20on%20screen%2C%20contemporary%20workspace%20with%20natural%20lighting&width=400&height=500&seq=testimonial-1&orientation=portrait',
      quote: 'Titilayo\'s business consulting services helped us secure funding and scale our operations. Her insights were invaluable.',
      rating: 5,
    },
    {
      name: 'Funmi Adeleke',
      role: 'Health & Wellness Brand Owner',
      service: 'Wellness Coaching',
      image: 'https://readdy.ai/api/search-image?query=African%20woman%20wellness%20entrepreneur%20in%20activewear%20smiling%20warmly%20in%20bright%20healthy%20lifestyle%20setting%2C%20radiating%20positive%20energy%20and%20wellbeing%2C%20yoga%20mat%20and%20plants%20nearby%2C%20serene%20peaceful%20atmosphere%20showing%20health%20coaching%20success%20story&width=400&height=500&seq=testimonial-2&orientation=portrait',
      quote: 'The wellness coaching changed my life. I\'m more energized, focused, and my business has never been better. Balance is possible!',
      rating: 5,
    },
    {
      name: 'Emeka Okafor',
      role: 'Online Retailer',
      service: 'E-commerce Management',
      image: 'https://readdy.ai/api/search-image?query=confident%20African%20male%20online%20store%20owner%20managing%20inventory%20on%20computer%20in%20organized%20warehouse%20setting%2C%20professional%20attire%2C%20products%20visible%20in%20background%2C%20efficient%20ecommerce%20business%20operation%20atmosphere&width=400&height=500&seq=testimonial-3&orientation=portrait',
      quote: 'My conversion rates doubled after implementing Titilayo\'s e-commerce strategies. She knows the game inside out.',
      rating: 5,
    },
    {
      name: 'Blessing Nwankwo',
      role: 'Fashion Entrepreneur',
      service: 'Business Consulting',
      image: 'https://readdy.ai/api/search-image?query=stylish%20African%20woman%20fashion%20entrepreneur%20in%20creative%20studio%20workspace%20surrounded%20by%20fabric%20samples%20and%20design%20materials%2C%20confident%20artistic%20expression%2C%20modern%20bright%20space%20showing%20successful%20creative%20business&width=400&height=500&seq=testimonial-4&orientation=portrait',
      quote: 'From struggling to thriving – that\'s my story with ThriveEdge. The strategic planning sessions gave me clarity and direction.',
      rating: 5,
    },
    {
      name: 'Daniel Ikechukwu',
      role: 'Digital Marketing Agency Owner',
      service: 'Business Consulting',
      image: 'https://readdy.ai/api/search-image?query=professional%20African%20male%20digital%20marketer%20working%20at%20desk%20with%20multiple%20monitors%20showing%20marketing%20analytics%20and%20social%20media%20campaigns%2C%20focused%20and%20productive%20expression%2C%20modern%20agency%20office%20environment&width=400&height=500&seq=testimonial-5&orientation=portrait',
      quote: 'Titilayo helped me streamline operations and increase profitability. Her approach is practical and results-driven.',
      rating: 5,
    },
    {
      name: 'Amina Yusuf',
      role: 'Wellness Coach & Entrepreneur',
      service: 'Wellness Coaching',
      image: 'https://readdy.ai/api/search-image?query=peaceful%20African%20woman%20wellness%20coach%20in%20meditation%20pose%20in%20bright%20natural%20setting%20with%20plants%2C%20calm%20serene%20expression%20radiating%20inner%20peace%2C%20healthy%20lifestyle%20and%20holistic%20wellbeing%20atmosphere&width=400&height=500&seq=testimonial-6&orientation=portrait',
      quote: 'Learning to prioritize my health while growing my business was a game-changer. Thank you for the guidance and support!',
      rating: 5,
    },
  ];

  const stats = [
    { number: '200+', label: 'Clients Served' },
    { number: '98%', label: 'Satisfaction Rate' },
    { number: '500+', label: 'Hours Coached' },
    { number: '150%', label: 'Avg Revenue Growth' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero */}
        <section ref={heroRef} className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center space-x-2 bg-accent-light-sage px-4 py-2 rounded-full mb-6">
                <i className="ri-trophy-line text-primary-sage"></i>
                <span className="text-sm font-medium text-primary-sage">Client Transformations</span>
              </div>

              <h1 className="font-serif font-bold text-5xl lg:text-6xl text-primary-charcoal mb-6">
                Real Results, Real Impact
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                See how ThriveEdge has helped businesses and individuals thrive
              </p>
            </motion.div>
          </div>
        </section>

        {/* Featured Case Study */}
        <section className="py-24 bg-primary-beige">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-5 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="lg:col-span-2"
              >
                <div className="relative rounded-3xl overflow-hidden h-[600px] shadow-2xl">
                  <img
                    src={featuredStory.image}
                    alt={featuredStory.name}
                    className="w-full h-full object-cover object-top grayscale hover:grayscale-0 transition-all duration-500"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="lg:col-span-3 bg-white rounded-3xl p-10 shadow-2xl"
              >
                <div className="inline-flex items-center space-x-2 bg-primary-gold/10 px-4 py-2 rounded-full mb-6">
                  <span className="text-sm font-semibold text-primary-gold">{featuredStory.service}</span>
                </div>

                <h2 className="font-serif font-bold text-4xl text-primary-charcoal mb-2">{featuredStory.name}</h2>
                <p className="text-gray-600 text-lg mb-6">{featuredStory.role}</p>

                <blockquote className="relative mb-8">
                  <i className="ri-double-quotes-l text-6xl text-primary-gold/20 absolute -top-6 -left-4"></i>
                  <p className="text-xl italic text-gray-700 leading-relaxed relative z-10 pl-8">
                    {featuredStory.quote}
                  </p>
                </blockquote>

                <div className="grid grid-cols-3 gap-6 p-6 bg-primary-beige rounded-2xl">
                  {featuredStory.results.map((result, idx) => (
                    <div key={idx} className="text-center">
                      <div className="text-4xl font-bold text-primary-gold mb-2">{result.metric}</div>
                      <div className="text-sm text-gray-600">{result.label}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Testimonial Grid */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <TestimonialCard key={index} testimonial={testimonial} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* Stats Banner */}
        <section ref={statsRef} className="py-24 bg-primary-sage">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={statsInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-5xl lg:text-6xl font-bold text-primary-gold mb-3">{stat.number}</div>
                  <div className="text-white text-lg">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-primary-gold">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center text-white">
            <h2 className="font-serif font-bold text-4xl lg:text-5xl mb-6">
              Ready to Write Your Success Story?
            </h2>
            <p className="text-xl mb-10">Join hundreds of satisfied clients who transformed their businesses and lives</p>
            
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-10 py-5 bg-white text-primary-charcoal hover:bg-primary-beige font-bold text-lg rounded-lg transition-all duration-300 shadow-xl hover:shadow-2xl cursor-pointer whitespace-nowrap"
            >
              Start Your Journey
              <i className="ri-arrow-right-line ml-3 text-xl"></i>
            </a>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 mt-10">
              <a href="mailto:titilayoajayi923@gmail.com" className="flex items-center space-x-2 text-white hover:text-white/80 transition-colors cursor-pointer">
                <i className="ri-mail-line text-xl"></i>
                <span>Email</span>
              </a>
              <a href="tel:+2349060814354" className="flex items-center space-x-2 text-white hover:text-white/80 transition-colors cursor-pointer">
                <i className="ri-phone-line text-xl"></i>
                <span>Phone</span>
              </a>
              <a href="https://wa.me/2349047390448" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-white hover:text-white/80 transition-colors cursor-pointer">
                <i className="ri-whatsapp-line text-xl"></i>
                <span>WhatsApp</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

const TestimonialCard = ({ testimonial, index }: { testimonial: any; index: number }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -8 }}
      className="bg-primary-beige rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
    >
      <div className="relative h-64">
        <img
          src={testimonial.image}
          alt={testimonial.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-xs font-semibold text-primary-gold">
          {testimonial.service}
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-center space-x-1 mb-4">
          {[...Array(testimonial.rating)].map((_, i) => (
            <i key={i} className="ri-star-fill text-primary-gold"></i>
          ))}
        </div>

        <blockquote className="text-gray-700 leading-relaxed mb-4 italic">
          "{testimonial.quote}"
        </blockquote>

        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 flex items-center justify-center bg-primary-sage rounded-full text-white font-bold">
            {testimonial.name.charAt(0)}
          </div>
          <div>
            <div className="font-semibold text-primary-charcoal">{testimonial.name}</div>
            <div className="text-sm text-gray-600">{testimonial.role}</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SuccessStoriesPage;
